import { computed, effect, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { getUserFromSnapshot } from '../../utils/user.util';
import { User } from '../../models/user.model';

export const AUTH_PATHS = new InjectionToken<{ onLogin: string; onLogout: string }>('AUTH_PATHS');

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);
  private paths = inject(AUTH_PATHS, { optional: true });

  readonly isLoggedIn = signal<boolean | null>(null);
  readonly user = signal<User | null>(null);
  readonly loginError = signal<string | null>(null);

  readonly isAdmin = computed(() => this.user()?.isAdmin ?? false);

  constructor() {
    effect(() => {
      if (this.isLoggedIn() === true) {
        console.log('login success');
      }
    });
  }

  async login(username: string, password: string): Promise<void> {
    try {
      const creds = await firstValueFrom(this.authService.login(username, password));
      const snapshot = await firstValueFrom(this.userService.getUser(creds.user.uid));
      this.user.set(getUserFromSnapshot(snapshot));
      this.isLoggedIn.set(true);
      this.loginError.set(null);
      if (this.paths?.onLogin) {
        this.router.navigate([this.paths.onLogin]);
      }
    } catch (error) {
      this.isLoggedIn.set(false);
      this.user.set(null);
      this.loginError.set(error as any);
    }
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.authService.logout());
    this.isLoggedIn.set(false);
    this.user.set(null);
    this.loginError.set(null);
    if (this.paths?.onLogout) {
      this.router.navigate([this.paths.onLogout]);
    }
  }

  async rehydrate(userId: string): Promise<void> {
    try {
      const snapshot = await firstValueFrom(this.userService.getUser(userId));
      this.user.set(getUserFromSnapshot(snapshot));
      this.isLoggedIn.set(true);
      this.loginError.set(null);
    } catch {
      this.isLoggedIn.set(false);
    }
  }

  setRehydrateFailed(): void {
    this.isLoggedIn.set(false);
  }
}
